import axios, { AxiosError, AxiosResponse } from 'axios'

import { routes } from 'helpers/routes'
import { setSessionStorage } from 'helpers/common'
import * as redirect from 'helpers/redirect'
import { forceSessionCleanup } from 'helpers/forceSessionCleanup'
import { getOAuthAuthorizationCode } from 'helpers/oauthQuery'

export const ERROR = {
  EXCHANGE_TOKEN: 'ErrorExchangeToken',
  VERIFY_TOKEN: 'ErrorVerifyToken',
}

const ignoredPathRedirectPaths = new Set<string>([
  '/',
  '/login',
  routes.login.path,
  routes.pdpa.path,
  ...Object.values(routes.error),
])

const getRequestUrl = (error: AxiosError): string =>
  `${error.config?.baseURL || ''}${error.config?.url || ''}`

export const handleAxiosResponseSuccess = (
  res: AxiosResponse,
): AxiosResponse => {
  const pathname = window.location.pathname
  const shouldIgnorePathRedirect =
    ignoredPathRedirectPaths.has(pathname) ||
    Boolean(getOAuthAuthorizationCode())

  if (!shouldIgnorePathRedirect) {
    setSessionStorage('pathRedirect', pathname)
  }

  return res
}

export const handleAxiosResponseError = async (
  error: Error | AxiosError,
): Promise<never | void> => {
  if (axios.isAxiosError(error) && error.response) {
    const resError = error.response
    const status = Number(resError.status) || 500
    const requestUrl = getRequestUrl(error)
    const isUrlQrCode = requestUrl.search(/qr-code/) > 0
    const isExchangeToken = requestUrl.search(/\/v1\/auth\/exchange-token/) >= 0
    const isTaskPdfReport = requestUrl.search(/\/v2\/report\/task/) >= 0
    const isTokenError = Object.values(ERROR).includes(resError.data?.code)

    if (isExchangeToken) {
      throw error
    }

    if (isTaskPdfReport && status !== 401) {
      throw error
    }

    if (status === 404 && isUrlQrCode) {
      return
    }

    const logAndThrow = (action: string): never => {
      console.error('[axios] response error', {
        action,
        status,
        apiCode: resError.data?.code,
      })
      throw error
    }

    if (isTokenError) {
      redirect.redirectToSSOLogin()

      return logAndThrow('redirect-iam')
    }

    switch (status) {
      case 401:
        forceSessionCleanup()

        return logAndThrow('unauthorized')

      case 503:
        window.location.replace(routes.error[503])

        return logAndThrow('error-page')

      case 403:
      case 404:
      case 500:
      case 502:
      case 504: {
        const statusRouteError = `${status}` as keyof typeof routes.error
        window.location.replace(routes.error[statusRouteError])

        return logAndThrow('error-page')
      }

      default:
        window.location.replace(routes.error[500])

        return logAndThrow('error-page')
    }
  }

  throw error
}

const config = (): void => {
  // Set up env config
  window._env_ = {
    ...window._env_,
    ...(process?.env && process.env),
  }

  // Set up i18n
  ;async () => {
    await import('./i18n')
  }

  // Set up axios config
  axios.defaults.timeout = 30000
  axios.defaults.withCredentials = true
  axios.interceptors.response.use(
    handleAxiosResponseSuccess,
    handleAxiosResponseError,
  )
}

export default config
